import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { GitDiff, Plus, Minus, ArrowRight, ArrowsLeftRight, Sparkle } from "@phosphor-icons/react";

/** Side-by-side diff of two chosen approved snapshots for an engagement. Defaults to
 * the two most recent; partners can swap either side via the picker to review changes
 * across multiple client updates. */
export default function SnapshotCompare({ engagementId, refreshKey, history }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentId, setCurrentId] = useState("");
  const [previousId, setPreviousId] = useState("");
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);

  // Seed the picker with the newest two history entries on first load / refresh.
  useEffect(() => {
    if (!history || history.length === 0) { setCurrentId(""); setPreviousId(""); return; }
    setCurrentId((prev) => prev && history.some((h) => h.id === prev) ? prev : history[0]?.id || "");
    setPreviousId((prev) => prev && history.some((h) => h.id === prev) ? prev : (history[1]?.id || history[0]?.id || ""));
  }, [history, refreshKey]);

  useEffect(() => {
    if (!engagementId) return;
    setLoading(true); setError("");
    const params = new URLSearchParams();
    if (currentId) params.set("current_id", currentId);
    if (previousId) params.set("previous_id", previousId);
    const qs = params.toString() ? `?${params.toString()}` : "";
    api.get(`/engagements/${engagementId}/snapshots/compare${qs}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load comparison"))
      .finally(() => setLoading(false));

    // AI digest — kicks off in parallel; slower path, so it doesn't block the diff.
    setDigest(null); setDigestLoading(true);
    api.get(`/engagements/${engagementId}/snapshots/compare/digest${qs}`)
      .then((r) => setDigest(r.data?.digest || null))
      .catch(() => setDigest(null))
      .finally(() => setDigestLoading(false));
  }, [engagementId, currentId, previousId, refreshKey]);

  const swap = () => {
    setCurrentId(previousId);
    setPreviousId(currentId);
  };

  const fmtOption = (h) => {
    const when = String(h.created_at || "").slice(0, 16).replace("T", " ");
    const title = h.title || h.engagement_name || "(untitled)";
    return `${when} · ${title.slice(0, 44)}${title.length > 44 ? "…" : ""}`;
  };
  const fmtDate = (iso) => String(iso || "").slice(0, 16).replace("T", " ") + " UTC";

  // Picker row — always show when we have any history, even during load, so the
  // partner never sees a jumping UI.
  const picker = (history?.length || 0) > 0 && (
    <div className="border border-border p-3 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-2 items-end" data-testid="snapshot-compare-picker">
      <div>
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Previous (older) snapshot</div>
        <select
          data-testid="compare-picker-previous"
          value={previousId}
          onChange={(e) => setPreviousId(e.target.value)}
          className="w-full rounded-none bg-secondary/40 border border-border text-xs px-2 py-2 text-foreground"
        >
          {history.map((h) => (
            <option key={h.id} value={h.id} disabled={h.id === currentId}>{fmtOption(h)}</option>
          ))}
        </select>
      </div>
      <button
        onClick={swap}
        data-testid="compare-picker-swap"
        title="Swap sides"
        disabled={!currentId || !previousId || currentId === previousId}
        className="border border-border p-2 text-muted-foreground hover:text-primary hover:border-primary/60 disabled:opacity-40 md:mb-0.5"
      >
        <ArrowsLeftRight size={14} weight="bold" />
      </button>
      <div>
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-1">Current (newer) snapshot</div>
        <select
          data-testid="compare-picker-current"
          value={currentId}
          onChange={(e) => setCurrentId(e.target.value)}
          className="w-full rounded-none bg-secondary/40 border border-primary/40 text-xs px-2 py-2 text-foreground"
        >
          {history.map((h) => (
            <option key={h.id} value={h.id} disabled={h.id === previousId}>{fmtOption(h)}</option>
          ))}
        </select>
      </div>
    </div>
  );

  if (loading && !data) return <div className="space-y-3">{picker}<div className="text-sm text-muted-foreground py-8 text-center">Loading diff…</div></div>;
  if (error) return <div className="space-y-3">{picker}<div className="text-sm text-destructive py-4">{error}</div></div>;
  if (!data) return null;

  if (!data.available && data.reason === "no_snapshots") {
    return (
      <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border" data-testid="compare-empty-none">
        No approved snapshots yet. Export at least two from the Draft tab to see what changed between them.
      </div>
    );
  }
  if (!data.available && data.reason === "single_snapshot") {
    return (
      <div className="space-y-3">
        {picker}
        <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border" data-testid="compare-empty-single">
          Only one approved snapshot so far ({data.current?.title || data.current?.engagement_name}). Export a second one to unlock the side-by-side comparison.
        </div>
      </div>
    );
  }

  const { current, previous, diff } = data;

  const digestCard = (digestLoading || digest) && (
    <div
      className="border-l-2 border-primary/60 bg-primary/5 px-4 py-3 flex items-start gap-2"
      data-testid="snapshot-compare-digest"
    >
      <Sparkle size={14} weight="fill" className="text-primary mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-1">YadidAi · what changed</div>
        {digestLoading && !digest && (
          <div className="text-xs text-muted-foreground italic">Reading the diff and drafting a one-liner…</div>
        )}
        {digest && (
          <div className="text-sm leading-relaxed text-foreground" data-testid="snapshot-compare-digest-text">{digest}</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="snapshot-compare">
      {picker}
      {digestCard}

      {/* Header row: side labels + timestamps */}
      <div className="grid grid-cols-2 gap-3">
        <div className="border border-border p-3 bg-secondary/30" data-testid="compare-previous-header">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Previous</div>
          <div className="text-sm font-medium mt-0.5 truncate">{previous.title || previous.engagement_name}</div>
          <div className="text-[0.625rem] font-mono text-muted-foreground mt-0.5">{fmtDate(previous.created_at)} · by {previous.created_by_email || "—"}</div>
        </div>
        <div className="border border-primary/50 p-3 bg-primary/5" data-testid="compare-current-header">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary flex items-center gap-1">
            <GitDiff size={11} weight="bold" /> Current
          </div>
          <div className="text-sm font-medium mt-0.5 truncate">{current.title || current.engagement_name}</div>
          <div className="text-[0.625rem] font-mono text-muted-foreground mt-0.5">{fmtDate(current.created_at)} · by {current.created_by_email || "—"}</div>
        </div>
      </div>

      {/* Title/subtitle diff */}
      {(diff.title_changed || diff.subtitle_changed) && (
        <div className="border border-border p-3">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Document header</div>
          {diff.title_changed && (
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <div className="text-[0.5625rem] font-mono uppercase tracking-widest text-muted-foreground">Title (was)</div>
                <div className="text-xs mt-0.5 line-through text-destructive/80" data-testid="compare-title-prev">{previous.title || "—"}</div>
              </div>
              <div>
                <div className="text-[0.5625rem] font-mono uppercase tracking-widest text-primary">Title (now)</div>
                <div className="text-xs mt-0.5 text-foreground font-medium" data-testid="compare-title-cur">{current.title || "—"}</div>
              </div>
            </div>
          )}
          {diff.subtitle_changed && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[0.5625rem] font-mono uppercase tracking-widest text-muted-foreground">Subtitle (was)</div>
                <div className="text-xs mt-0.5 line-through text-destructive/80" data-testid="compare-subtitle-prev">{previous.subtitle || "—"}</div>
              </div>
              <div>
                <div className="text-[0.5625rem] font-mono uppercase tracking-widest text-primary">Subtitle (now)</div>
                <div className="text-xs mt-0.5 text-foreground" data-testid="compare-subtitle-cur">{current.subtitle || "—"}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sections diff — one card per section, side-by-side */}
      <div className="space-y-3">
        {diff.sections.map((s, i) => (
          <div
            key={i}
            className={`border p-3 ${s.is_new ? "border-emerald-500/50 bg-emerald-500/5" : s.is_removed ? "border-destructive/50 bg-destructive/5" : "border-border"}`}
            data-testid={`compare-section-${i}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[0.6875rem] font-mono uppercase tracking-widest text-primary">{s.label}</div>
              {s.is_new && <span className="text-[0.5625rem] font-mono uppercase tracking-widest text-emerald-500 border border-emerald-500/50 px-1.5 py-0.5">+ New section</span>}
              {s.is_removed && <span className="text-[0.5625rem] font-mono uppercase tracking-widest text-destructive border border-destructive/50 px-1.5 py-0.5">− Removed</span>}
              {!s.is_new && !s.is_removed && !s.summary_changed && s.highlights_added.length === 0 && s.highlights_removed.length === 0 && (
                <span className="text-[0.5625rem] font-mono uppercase tracking-widest text-muted-foreground">Unchanged</span>
              )}
            </div>

            {/* Summary side-by-side */}
            {(s.summary_previous || s.summary_current) && (
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="text-xs leading-relaxed">
                  <div className="text-[0.5625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Summary — was</div>
                  <div className={s.summary_changed && s.summary_previous ? "text-destructive/80" : "text-muted-foreground"} data-testid={`compare-summary-prev-${i}`}>
                    {s.summary_previous || <em className="opacity-60">(empty)</em>}
                  </div>
                </div>
                <div className="text-xs leading-relaxed">
                  <div className="text-[0.5625rem] font-mono uppercase tracking-widest text-primary mb-1">Summary — now</div>
                  <div className={s.summary_changed ? "text-foreground font-medium" : "text-muted-foreground"} data-testid={`compare-summary-cur-${i}`}>
                    {s.summary_current || <em className="opacity-60">(empty)</em>}
                  </div>
                </div>
              </div>
            )}

            {/* Highlights: added / removed / kept */}
            {(s.highlights_added.length > 0 || s.highlights_removed.length > 0 || s.highlights_kept.length > 0) && (
              <div className="space-y-1">
                {s.highlights_added.map((h, hi) => (
                  <div key={`a${hi}`} className="text-xs flex items-start gap-1.5 text-emerald-500" data-testid={`compare-hl-added-${i}-${hi}`}>
                    <Plus size={11} weight="bold" className="mt-0.5 shrink-0" />
                    <span className="leading-relaxed">{h}</span>
                  </div>
                ))}
                {s.highlights_removed.map((h, hi) => (
                  <div key={`r${hi}`} className="text-xs flex items-start gap-1.5 text-destructive line-through opacity-80" data-testid={`compare-hl-removed-${i}-${hi}`}>
                    <Minus size={11} weight="bold" className="mt-0.5 shrink-0 no-underline" />
                    <span className="leading-relaxed">{h}</span>
                  </div>
                ))}
                {s.highlights_kept.map((h, hi) => (
                  <div key={`k${hi}`} className="text-xs flex items-start gap-1.5 text-muted-foreground" data-testid={`compare-hl-kept-${i}-${hi}`}>
                    <ArrowRight size={11} weight="regular" className="mt-0.5 shrink-0" />
                    <span className="leading-relaxed">{h}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
